import React from 'react';
import { IconCross, IconSmile, InputText } from '../..';
import { UseAutoComplete } from '../../../modules/GeneralFlow/hooks/useAutoComplete';
import './InputAutoComplete.scss';
import { Translate } from 'react-localize-redux-dep-updated';
import { translate } from '../../../utils/localization';
import cn from 'classnames';
import { SheetModal } from '../layout/sheetlayout';
import { AddressBook } from './AddressBook';
import { ContactCreateParams, GeoAutocompleteAddress } from '@brenger/api-client';
import { useHighlight } from '../../../hooks/useHighlight';
import { useDispatch } from 'react-redux';
import { actions } from '../../../modules/GeneralFlow/ducks';

const iconStyle = {
  width: '15px',
  height: '15px',
  borderRadius: '9999px',
  border: '4px solid #0073fa',
};

export const AutoCompleteInput: React.FC<
  UseAutoComplete & { stopType: 'pickup' | 'delivery'; showAddressBook?: boolean }
> = props => {
  const target = props.stopType === 'delivery' ? 'to' : 'from';
  const translationKey = `request_flow.widget.fields.${target}.label_short`;
  const id = 'auto_complete_input_' + props.stopType;
  const [isFocused, setIsFocused] = React.useState(false);
  const [showModal, setShowModal] = React.useState(false);
  const handleBlur = React.useRef(true);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const wrapper = document.getElementById(id)?.closest('.input--auto-complete--wrapper') as HTMLElement | null;
      if (wrapper && !wrapper.contains(event.target as Node)) {
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [id]);

  // Time out is needed because this could be fired quickly after a selection, on blur for example.
  const handleDefaultSelection = (): void => {
    setTimeout(() => {
      if (handleBlur.current) {
        if (props.results.length && props.query.length > 2) props.setPlaceId(props.results[0].place_id);
      } else {
        handleBlur.current = true;
      }
    }, 200);
  };

  const openAddressBook = (): void => {
    handleBlur.current = false;
    setShowModal(true);
  };
  const highlightActions = props.results.map(result => ({ action: () => props.setPlaceId(result.place_id) }));
  if (props.showAddressBook) {
    highlightActions.push({ action: openAddressBook });
  }
  const highlight = useHighlight(highlightActions);
  const dispatch = useDispatch();

  return (
    <div className="input--auto-complete--wrapper" onFocus={() => setIsFocused(true)}>
      <InputText
        autoComplete="off"
        placeholder={translate('request_flow.widget.fields.to.place_holder')}
        id={id}
        value={props.query}
        onChange={v => {
          highlight.reset();
          props.setQuery(v);
        }}
        isLoading={props.isLoading}
        onBlur={handleDefaultSelection}
        onKeyDown={e => {
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            highlight.prev();
          }
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            highlight.next();
          }
          if (e.key === 'Enter') highlight.select();
        }}
      />
      <div className="input--auto-complete--prefix">
        <div style={{ width: '15px', display: 'inline-block' }}>
          {props.isLoading && <IconSmile spinning={true} />}
          {!props.isLoading && (
            <div style={{ ...iconStyle, backgroundColor: props.stopType === 'delivery' ? '#0073fa' : undefined }} />
          )}
        </div>
        <b className="input--auto-complete--direction">
          <Translate id={translationKey} />
        </b>
      </div>
      {!props.isLoading && props.query && (
        <button
          /**
           * This is convenience:
           * a cross to clear input is nice, but it should not be the next thing to focus.
           * We use tabIndex to prevent this and then hitting tab hops to the next input.
           * */
          tabIndex={-1}
          type="button"
          style={{
            position: 'absolute',
            right: '1rem',
            top: '16px',
            appearance: 'none',
            border: 'none',
            background: 'none',
          }}
          onClick={() => {
            props.reset();
            highlight.reset();
            document.getElementById(id)?.focus();
          }}
        >
          <IconCross style={{ width: '11px', height: '11px' }} />
        </button>
      )}

      {isFocused && !props.isLoading && props.results.length > 0 && (
        <div className="input--auto-complete--feedback">
          {props.results.map((result, i) => (
            <button
              type="button"
              className={cn('input--auto-complete--result', {
                'input--auto-complete--result--highlighted': highlight.current === i,
              })}
              key={result.place_id}
              onClick={e => {
                handleBlur.current = false;
                e.stopPropagation();
                props.setPlaceId(result.place_id);
              }}
            >
              <div>{result.label}</div>
            </button>
          ))}
          {props.showAddressBook && (
            <button
              type="button"
              className={cn('input--auto-complete--result text-primary', {
                'input--auto-complete--result--highlighted': highlight.current === props.results.length,
              })}
              onClick={openAddressBook}
            >
              <Translate id="address_book.list_action" />
            </button>
          )}
        </div>
      )}
      {isFocused && props.noResults && (
        <div className="input--auto-complete--feedback" style={{ padding: '10px 15px' }}>
          <b>
            <Translate id="request_flow.widget.no_matches_title" data={{ search_term: <i>{props.query}</i> }} />
          </b>
          <br />
          <Translate id="request_flow.widget.no_matches_body" />
        </div>
      )}
      <SheetModal isActive={showModal} closeHandler={() => setShowModal(false)}>
        <AddressBook
          handleClose={() => setShowModal(false)}
          onSelect={ha => {
            setShowModal(false);
            props.setQuery(null);
            props.setNewAddress(ha.address as GeoAutocompleteAddress);
            if (ha.contact)
              dispatch(
                actions.setContactDetails({
                  contact_type: props.stopType,
                  details: ha.contact as ContactCreateParams,
                })
              );
            if (ha.instructions)
              dispatch(
                actions.setStopAddress({
                  stopType: props.stopType,
                  instructions: ha.instructions,
                })
              );
          }}
        />
      </SheetModal>
    </div>
  );
};
